<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import Table from "../../../base-components/Table";
import Preview from "../../../base-components/Preview";
import { FormInput, FormSelect } from "../../../base-components/Form";
import LoadingIcon from "../../../base-components/LoadingIcon";
import { useMesasStore } from "../../../services/mesas";
import { useSectoresStore } from "../../../services/sectores";
import type { Mesa } from "@fixeduy/restaurantestypes";
import ModalMesasEditar from "./modals/ModalMesasEditar.vue";
import { TIPOS_REGISTRO_TURNO } from "../../../constants/tiposRegistroTurno";

const mesasStore = useMesasStore();
const sectoresStore = useSectoresStore();

onBeforeMount(() => {
  mesasStore.getFromFirestore();
  if (!sectoresStore.sectores.length) {
    sectoresStore.getFromFirestore();
  }
});

const inputBuscar = ref("");
const filtroEstado = ref("");
const mesaSeleccionada = ref<Mesa | null>(null);

const abrirEditar = (mesa: Mesa) => {
  mesaSeleccionada.value = mesa;
};

const cerrarModal = () => {
  mesaSeleccionada.value = null;
};

const opcionesEstado = [
  { value: "", label: "Todos" },
  { value: "Disponible", label: "Disponible" },
  { value: "Ocupada", label: "Ocupada" },
  { value: "Reservada", label: "Reservada" }
];

const filtrarMesa = (mesa: Mesa) => {
  const buscar = inputBuscar.value.toLocaleLowerCase();
  const cumpleBusqueda =
    buscar === "" || mesa.nombre.toLocaleLowerCase().includes(buscar);
  const estadoMesa = (mesa.estado || "").toLocaleLowerCase();
  const estadoFiltro = (filtroEstado.value || "").toLocaleLowerCase();
  const cumpleEstado = estadoFiltro === "" || estadoMesa === estadoFiltro;
  return cumpleBusqueda && cumpleEstado;
};

const mesasActivas = computed(() =>
  mesasStore.mesasOrdenadas.filter(m => m.activo)
);

const mesasVisibles = computed(() =>
  mesasActivas.value.filter(m => filtrarMesa(m))
);

const mostrarSinDatos = computed(
  () =>
    !mesasStore.cargando &&
    (mesasActivas.value.length === 0 || mesasVisibles.value.length === 0)
);

const mostrarEstadoVacio = computed(
  () =>
    !mesasStore.cargando &&
    mesasActivas.value.length > 0 &&
    mesasVisibles.value.length === 0
);

const badgeClassesPorEstadoMesa = (estado: string | undefined | null) => {
  const s = (estado ?? "").toLowerCase();

  const opt = (value: string) =>
    TIPOS_REGISTRO_TURNO.find(t => t.value === value)?.badgeClasses ?? "";

  if (s === "disponible") return opt("ingreso");
  if (s === "ocupada" || s === "ocupado") return opt("salida");
  if (s === "reservada") return opt("descansoFin");

  return "";
};

const formatearEstadoMesa = (estado: string | undefined | null) => {
  const s = (estado ?? "").toString().trim();
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
};

const obtenerNombreSector = (sectorId?: string | null) => {
  if (!sectorId) return "-";
  return sectoresStore.sectores.find(s => s.id === sectorId)?.nombre ?? "-";
};
</script>

<template>
  <Preview class="box">
    <div class="grid grid-cols-3 gap-4">
      <div class="col-span-2 p-5">
        <h2 class="text-base font-medium">Mesas</h2>
        <FormInput
          class="mt-2"
          type="text"
          placeholder="Buscar por nombre..."
          v-model="inputBuscar"
        />
      </div>
      <div class="p-5">
        <label
          class="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1"
        >
          Estado
        </label>
        <FormSelect v-model="filtroEstado" class="mt-1 w-full">
          <option
            v-for="opt in opcionesEstado"
            :key="opt.value || 'todos'"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </FormSelect>
      </div>
    </div>
    <div class="px-5">
      <Preview.Panel>
        <Table hover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th class="whitespace-nowrap">Nombre</Table.Th>
              <Table.Th class="whitespace-nowrap">Capacidad</Table.Th>
              <Table.Th class="whitespace-nowrap">Sector</Table.Th>
              <Table.Th class="whitespace-nowrap">Estado</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr
              v-for="mesa in mesasVisibles"
              :key="mesa.id"
              class="cursor-pointer"
              @click="abrirEditar(mesa)"
            >
              <Table.Td>{{ mesa.nombre }}</Table.Td>
              <Table.Td>{{ mesa.capacidad }}</Table.Td>
              <Table.Td>{{ obtenerNombreSector(mesa.sectorId) }}</Table.Td>
              <Table.Td>
                <span
                  class="inline-flex px-2 py-1 text-xs font-medium rounded border whitespace-nowrap"
                  :class="badgeClassesPorEstadoMesa(mesa.estado)"
                >
                  {{ formatearEstadoMesa(mesa.estado) }}
                </span>
              </Table.Td>
            </Table.Tr>
            <Table.Tr v-if="mesasStore.cargando">
              <Table.Td colspan="3">
                <div class="w-8 mx-auto">
                  <LoadingIcon icon="oval" class="w-8 h-8" />
                </div>
                <div class="w-auto mx-auto text-center mt-2">Cargando...</div>
              </Table.Td>
            </Table.Tr>
            <Table.Tr v-else-if="mostrarSinDatos">
              <Table.Td colspan="3" class="text-center py-8 text-slate-500">
                {{
                  mostrarEstadoVacio
                    ? "Ninguna mesa coincide con el filtro."
                    : "No hay mesas para mostrar."
                }}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Preview.Panel>
    </div>
    <ModalMesasEditar
      v-if="mesaSeleccionada"
      :mesa="mesaSeleccionada"
      @cerrar="cerrarModal"
    />
  </Preview>
</template>
